import React, { useState, useEffect } from 'react';
import DynamicSection from '../components/dynamic/DynamicSection';
import DynamicCard from '../components/dynamic/DynamicCard';
import { certificationsAPI } from '../services/api';
import { Certification } from '../types';
import { FaCertificate, FaCalendar, FaExternalLinkAlt } from 'react-icons/fa';

const Certifications: React.FC = () => {
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadCertifications = async () => {
      try {
        const response = await certificationsAPI.getAll();
        if (response && response.data) {
          setCertifications(Array.isArray(response.data) ? response.data : []);
        } else {
          setCertifications([]);
        }
      } catch (error: any) {
        console.error('Error loading certifications:', error);
        setCertifications([]);
      } finally {
        setLoading(false);
      }
    };
    loadCertifications();
  }, []);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-xl text-gray-600 dark:text-gray-400">Loading certifications...</div>
      </div>
    );
  }

  return (
    <div className="pt-20 bg-white dark:bg-gray-900 min-h-screen">
      <DynamicSection
        title="Certifications & Achievements"
        subtitle="Professional certifications and qualifications"
      >
        {certifications.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">No certifications found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {certifications.map((cert) => (
            <DynamicCard key={cert.id} className="p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                    <FaCertificate className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
                <div className="flex-grow">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {cert.name}
                  </h3>
                  <p className="text-primary-600 dark:text-primary-400 font-semibold mb-3">
                    {cert.issuer}
                  </p>
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-3">
                    <FaCalendar className="mr-2" />
                    <span>
                      Issued: {new Date(cert.issueDate).toLocaleDateString()}
                      {cert.expiryDate && 
                        ` • Expires: ${new Date(cert.expiryDate).toLocaleDateString()}`
                      }
                    </span>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    {cert.description}
                  </p>
                  {cert.credentialId && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Credential ID: {cert.credentialId}
                    </p>
                  )}
                  {cert.credentialUrl && (
                    <a
                      href={cert.credentialUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center"
                    >
                      Verify Credential
                      <FaExternalLinkAlt className="ml-2" />
                    </a>
                  )}
                  {cert.skills.length > 0 && (
                    <div className="mt-4">
                      <div className="flex flex-wrap gap-2">
                        {cert.skills.map((skill) => (
                          <span
                            key={skill}
                            className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full text-sm"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </DynamicCard>
            ))}
          </div>
        )}
      </DynamicSection>
    </div>
  );
};

export default Certifications;
